import { useCrossFlow } from '@atlassiansox/cross-flow-api-internals';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useUserHasAddProductPermissions } from './user-product-permissions.tsx';
import { isSupportedIssueType } from './confluence-templates.tsx';

export type PlaceholderTargeting =
	| 'RequestTrial'
	| 'CrossJoin'
	| 'CoUse'
	| 'CrossFlow-User'
	| 'CrossFlow-Admin'
	| undefined;

export const useTargeting = (
	projectType: string | undefined,
	issueTypeEnglish: string | undefined,
	hasActivatedConfluenceWithinFourteenDays: boolean,
	hasConfluenceAccess: boolean,
	isConfluenceProvisioningStarted?: boolean,
): PlaceholderTargeting => {
	const licensedProducts = useLicensedProducts();
	const crossFlow = useCrossFlow();
	const isSiteAdmin = useIsSiteAdmin();

	// areProductSuggestionsEnabled does not have a specific hook and we are required to use the deprecated interface
	const { areProductSuggestionsEnabled } = useTenantContext();
	const tenantHasConfluence = licensedProducts?.confluence === true;

	// checking if end user is TEL eligible
	const { canProvisionConfluenceEndUser } = useUserHasAddProductPermissions({
		skip: isSiteAdmin || tenantHasConfluence || !areProductSuggestionsEnabled,
	});

	if (projectType !== SOFTWARE_PROJECT) return undefined;

	if (!isSupportedIssueType(issueTypeEnglish)) return undefined;

	// if provisioning has started but wasn't completed, we don't want to show cross-flow
	if (!tenantHasConfluence && !isConfluenceProvisioningStarted) {
		if (!crossFlow.isEnabled || !areProductSuggestionsEnabled) return undefined;

		// canProvisionConfluence wraps the admin check
		if (isSiteAdmin) return 'CrossFlow-Admin';

		// end-users TEL
		if (canProvisionConfluenceEndUser) return 'CrossFlow-User';

		return 'RequestTrial';
	}

	if (hasConfluenceAccess && hasActivatedConfluenceWithinFourteenDays) return 'CoUse';

	return undefined;
};
